import axios from "axios";
import Cookies from "js-cookie";
import { ChevronDown, Search } from "lucide-react";
import React, { useEffect, useState } from "react";
import { getImageUrl } from "../../utils";
import { adminTokenCookieName, envs } from "../../utils/constants";
import { Widget } from "../../widgetList/widgetList";

// Type definitions
type BlogSelectionType = "all" | "select";
type PaginationType = "infinite" | "pages";
type LayoutType = "full" | "container";

interface Blog {
	id: number;
	title: string;
	slug: string;
	featuredImage: string;
}

interface WidgetConfig {
	blogSelection: BlogSelectionType;
	selectedBlogs: Blog[];
	pagination: PaginationType;
	layout: LayoutType;
	sectionTitle: string;
}

interface BlogWidgetFormProps {
	choosenItem: Widget;
	onUpdate: (w: Widget) => void;
	apiUrl?: string;
}

const BlogWidgetForm: React.FC<BlogWidgetFormProps> = ({
	choosenItem,
	onUpdate,
}) => {
	const initialConfig: WidgetConfig | undefined = choosenItem.data?.settings;
	const initialBlogs: Blog[] = (choosenItem.data as any)?.selectedBlogs || [];

	const [config, setConfig] = useState<WidgetConfig>({
		blogSelection: initialConfig?.blogSelection || "all",
		selectedBlogs: initialBlogs,
		pagination: initialConfig?.pagination || "infinite",
		layout: initialConfig?.layout || "container",
		sectionTitle: initialConfig?.sectionTitle || "Latest Posts",
	});

	const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [availableBlogs, setAvailableBlogs] = useState<Blog[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	// Mock API call for blogs
	const fetchBlogs = async (search: string = ""): Promise<void> => {
		setIsLoading(true);
		try {
			const response = await axios.get(
				`${
					envs.baseApi
				}blog-post?perPage=100&currentPage=${1}&search=${search}`,
				{
					headers: {
						Authorization: `Bearer ${Cookies.get(
							adminTokenCookieName
						)}`,
					},
				}
			);

			const data: Blog[] = await response.data?.data;

			setAvailableBlogs(
				data.map((blog: Blog) => ({
					featuredImage: blog.featuredImage,
					title: blog.title,
					slug: blog.slug,
					id: blog.id,
				}))
			);
		} catch (err) {
			console.error("Error fetching authors:", err);
		} finally {
			setIsLoading(false);
		}

		// setAvailableBlogs(filtered);
		setIsLoading(false);
	};

	useEffect(() => {
		if (config.blogSelection === "select") {
			fetchBlogs(searchTerm);
		}
	}, [config.blogSelection, searchTerm]);

	const handleBlogSelection = (blog: Blog): void => {
		const isSelected: boolean = config.selectedBlogs.some(
			(b: Blog) => b.id === blog.id
		);
		if (isSelected) {
			setConfig((prev: WidgetConfig) => ({
				...prev,
				selectedBlogs: prev.selectedBlogs.filter(
					(b: Blog) => b.id !== blog.id
				),
			}));
		} else {
			setConfig((prev: WidgetConfig) => ({
				...prev,
				selectedBlogs: [...prev.selectedBlogs, blog],
			}));
		}
	};

	const handleSave = (): void => {
		const { selectedBlogs, ...restSettings } = config;

		const updatedWidget = {
			...choosenItem,
			active: true,
			data: {
				settings: restSettings,
				...(restSettings.blogSelection === "select"
					? {
							selectedBlogs: selectedBlogs,
					  }
					: {}),
			},
		};
		onUpdate(updatedWidget);
	};

	return (
		<div className="flex gap-6">
			{/* Form Panel */}
			<div className="w-full h-fit">
				<h2 className="text-lg font-semibold text-gray-900 mb-6">
					Blog Widget Configuration
				</h2>

				<div className="space-y-6">
					{/* Section Title */}
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-2">
							Section Title
						</label>
						<input
							type="text"
							value={config.sectionTitle}
							onChange={(
								e: React.ChangeEvent<HTMLInputElement>
							) =>
								setConfig((prev) => ({
									...prev,
									sectionTitle: e.target.value,
								}))
							}
							className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
							placeholder="Enter section title"
						/>
					</div>

					{/* Blog Selection */}
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-3">
							Blog Selection
						</label>
						<div className="space-y-3">
							<label className="flex items-center">
								<input
									type="radio"
									name="blogSelection"
									value="all"
									checked={config.blogSelection === "all"}
									onChange={(
										e: React.ChangeEvent<HTMLInputElement>
									) =>
										setConfig((prev) => ({
											...prev,
											blogSelection: e.target
												.value as BlogSelectionType,
										}))
									}
									className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
								/>
								<span className="ml-2 text-sm text-gray-700">
									Show all blogs
								</span>
							</label>

							<label className="flex items-center">
								<input
									type="radio"
									name="blogSelection"
									value="select"
									checked={config.blogSelection === "select"}
									onChange={(
										e: React.ChangeEvent<HTMLInputElement>
									) =>
										setConfig((prev) => ({
											...prev,
											blogSelection: e.target
												.value as BlogSelectionType,
										}))
									}
									className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
								/>
								<span className="ml-2 text-sm text-gray-700">
									Select specific blogs
								</span>
							</label>
						</div>

						{config.blogSelection === "select" && (
							<div className="mt-3 relative">
								<div className="relative">
									<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
									<input
										type="text"
										placeholder="Search blogs..."
										value={searchTerm}
										onChange={(
											e: React.ChangeEvent<HTMLInputElement>
										) => setSearchTerm(e.target.value)}
										onClick={() => setIsDropdownOpen(true)}
										className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
									/>
									<ChevronDown
										className={`absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 transition-transform ${
											isDropdownOpen ? "rotate-180" : ""
										}`}
										onClick={() =>
											setIsDropdownOpen(!isDropdownOpen)
										}
									/>
								</div>

								{isDropdownOpen && (
									<div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
										{isLoading ? (
											<div className="p-3 text-center">
												<div className="flex items-center justify-center space-x-2">
													<div className="w-4 h-4 bg-blue-200 rounded-full animate-pulse"></div>
													<span className="text-sm text-gray-500">
														Loading...
													</span>
												</div>
											</div>
										) : availableBlogs.length === 0 ? (
											<div className="p-3 text-sm text-gray-500 text-center">
												No blogs found
											</div>
										) : (
											availableBlogs.map((blog: Blog) => (
												<div
													key={blog.id}
													onClick={() =>
														handleBlogSelection(
															blog
														)
													}
													className={`p-3 cursor-pointer hover:bg-gray-50 border-b last:border-b-0 ${
														config.selectedBlogs.some(
															(b) =>
																b.id === blog.id
														)
															? "bg-blue-50"
															: ""
													}`}>
													<div className="flex items-center gap-4">
														<img
															src={getImageUrl(
																blog.featuredImage
															)}
															alt={blog.title}
															className="w-8 h-8 rounded-full"
														/>
														<div className="flex-1">
															<div className="text-sm font-medium text-gray-900">
																{blog.title}
															</div>
															<div className="text-xs text-gray-500">
																{blog.slug}
															</div>
														</div>
														<input
															type="checkbox"
															checked={config.selectedBlogs.some(
																(b) =>
																	b.id ===
																	blog.id
															)}
															onChange={() => {}}
															className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
														/>
													</div>
												</div>
											))
										)}
									</div>
								)}

								{config.selectedBlogs.length > 0 && (
									<div className="mt-2 space-y-1">
										{config.selectedBlogs.map(
											(blog: Blog) => (
												<div
													key={blog.id}
													className="flex items-center justify-between bg-blue-50 px-2 py-1 rounded text-sm">
													<span className="text-blue-800">
														{blog.title}
													</span>
													<button
														onClick={() =>
															handleBlogSelection(
																blog
															)
														}
														className="text-blue-600 hover:text-blue-800">
														×
													</button>
												</div>
											)
										)}
									</div>
								)}
							</div>
						)}
					</div>

					{/* Pagination */}
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-3">
							Pagination Type
						</label>
						<div className="space-y-3">
							<label className="flex items-center">
								<input
									type="radio"
									name="pagination"
									value="infinite"
									checked={config.pagination === "infinite"}
									onChange={(
										e: React.ChangeEvent<HTMLInputElement>
									) =>
										setConfig((prev) => ({
											...prev,
											pagination: e.target
												.value as PaginationType,
										}))
									}
									className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
								/>
								<span className="ml-2 text-sm text-gray-700">
									Infinite scroll
								</span>
							</label>

							<label className="flex items-center">
								<input
									type="radio"
									name="pagination"
									value="pages"
									checked={config.pagination === "pages"}
									onChange={(
										e: React.ChangeEvent<HTMLInputElement>
									) =>
										setConfig((prev) => ({
											...prev,
											pagination: e.target
												.value as PaginationType,
										}))
									}
									className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
								/>
								<span className="ml-2 text-sm text-gray-700">
									Page numbers
								</span>
							</label>
						</div>
					</div>

					{/* Layout */}
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-3">
							Layout Width
						</label>
						<div className="space-y-3">
							<label className="flex items-center">
								<input
									type="radio"
									name="layout"
									value="container"
									checked={config.layout === "container"}
									onChange={(
										e: React.ChangeEvent<HTMLInputElement>
									) =>
										setConfig((prev) => ({
											...prev,
											layout: e.target
												.value as LayoutType,
										}))
									}
									className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
								/>
								<span className="ml-2 text-sm text-gray-700">
									Container width
								</span>
							</label>

							<label className="flex items-center">
								<input
									type="radio"
									name="layout"
									value="full"
									checked={config.layout === "full"}
									onChange={(
										e: React.ChangeEvent<HTMLInputElement>
									) =>
										setConfig((prev) => ({
											...prev,
											layout: e.target
												.value as LayoutType,
										}))
									}
									className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
								/>
								<span className="ml-2 text-sm text-gray-700">
									Full width
								</span>
							</label>
						</div>
					</div>

					{/* Action Buttons */}
					<div className="pt-4 border-t space-y-2">
						<button
							onClick={handleSave}
							className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors">
							Save Widget
						</button>
						<button className="w-full bg-gray-100 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors">
							Preview Full Size
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default BlogWidgetForm;
